import { Alert, Box, Stack, Typography } from '@mui/material';
import type { ApexPlan } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

/**
 * SASADM-0178 (SASATH-0395 §2) — a set-primary's apex plan: the tenants whose main address
 * moves to `appName` (preview) or moved (result), the skipped ones with why, and the tenants
 * where the previous app gets no own address. The lists may be cut; the counts are exact.
 */
export function ApexPlanView({
  plan,
  appName,
  fromApp,
  mode,
}: {
  plan: ApexPlan;
  appName: string;
  /** The app losing the main address (named in the not-provisioned warning). */
  fromApp?: string | undefined;
  mode: 'preview' | 'result';
}) {
  const { t } = useCopy();
  const preview = mode === 'preview';
  return (
    <Stack spacing={1.5}>
      {plan.movedCount === 0 ? (
        <Typography variant="body2">
          {preview
            ? t('products.apps.apexPlan.nothingMoves', "No tenant's main address will move.")
            : t('products.apps.apexPlan.nothingMoved', "No tenant's main address moved.")}
        </Typography>
      ) : (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {preview
              ? t('products.apps.apexPlan.willMove', '{{n}} tenant(s) will move to {{app}}', {
                  n: plan.movedCount,
                  app: appName,
                })
              : t('products.apps.apexPlan.moved', '{{n}} tenant(s) moved to {{app}}', {
                  n: plan.movedCount,
                  app: appName,
                })}
          </Typography>
          <PlanList
            items={plan.moved.map((m) => ({
              key: `m-${m.tenantId}-${m.hostname}`,
              tenant: m.tenantSlug,
              hostname: m.hostname,
              note: m.fromAppSlug
                ? t('products.apps.apexPlan.fromApp', 'from {{app}}', { app: m.fromAppSlug })
                : null,
            }))}
          />
        </Box>
      )}

      {plan.skippedCount > 0 ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('products.apps.apexPlan.skipped', '{{n}} skipped', { n: plan.skippedCount })}
          </Typography>
          <PlanList
            items={plan.skipped.map((s) => ({
              key: `s-${s.tenantId}-${s.hostname}`,
              tenant: s.tenantSlug,
              hostname: s.hostname,
              note: t(`products.apps.apexPlan.reasons.${s.reason}`, s.reason),
            }))}
          />
        </Box>
      ) : null}

      {plan.previousAppHostNotProvisioned.length > 0 ? (
        <Alert severity="warning">
          {t(
            'products.apps.apexPlan.notProvisioned',
            '{{app}} gets no own address on these tenants',
            {
              app: fromApp ?? t('products.apps.apexPlan.previousApp', 'The previous app'),
            },
          )}
          <PlanList
            items={plan.previousAppHostNotProvisioned.map((p) => ({
              key: `p-${p.tenantId}-${p.hostname}`,
              tenant: null,
              hostname: p.hostname,
              note: p.reason,
            }))}
          />
        </Alert>
      ) : null}

      {plan.truncated ? (
        <Typography variant="caption" color="text.secondary">
          {t(
            'products.apps.apexPlan.truncated',
            'Showing part of the list — the counts are exact.',
          )}
        </Typography>
      ) : null}
    </Stack>
  );
}

function PlanList({
  items,
}: {
  items: { key: string; tenant: string | null; hostname: string; note: string | null }[];
}) {
  if (items.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
      {items.map((item) => (
        <Typography key={item.key} component="li" variant="body2">
          {item.tenant ? `${item.tenant} — ` : null}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {item.hostname}
          </Box>
          {item.note ? ` (${item.note})` : null}
        </Typography>
      ))}
    </Box>
  );
}
